import React, { useCallback, useEffect } from 'react';
import dayjs from 'dayjs';
import { useSelector, useDispatch } from 'react-redux';
import { actions } from 'redux/packagesBySearchCode/slice';
import { actions as paquerActions } from 'redux/paquers/slice';
import { selectGlobals } from 'redux/globals/selectors';
import usePackagesBySearchCode from 'redux/packagesBySearchCode';
import {
  packagesBySearchCodeItemsSelector,
  packagesBySearchCodePageableSelector,
  packagesBySearchCodeLoadedSelector,
} from 'redux/packagesBySearchCode/selectors';
import PaqueryTable from '@paquery-team/lib-table';
import ViewPackageButton from 'components/viewPackageButton';
import { isFinalStatus, isReadyForDeliveryStatus } from 'constants/packages';
import SITELINKS from 'constants/sitelinks';
import { TableDateTimeFormat } from 'constants/dateFormats';
import externalCodeColumn from 'components/tables/columns';
import PaquerSettingsButton from 'components/paquerSettingsButton';
import usePaquers from 'redux/paquers';
import DownloadLabelButton from 'components/downloadLabelButton';
import SizeIndicator from 'components/sizeIndicator';
import EditRecordButton from 'components/editRecordButton';
import EditDropdownButton from 'components/editRecordButton/EditDropdownButton';
import DuplicatePackageButton from 'components/duplicatePackageButton';

const downloadLabelColumn = {
  render: (_, record) =>
    isFinalStatus(record.status) ? null : (
      <DownloadLabelButton
        packageId={record.key}
        externalCode={record.externalCode}
        marketplaceId={record.ownerID}
      />
    ),
};

const destinationAddressColumn = {
  title: 'Destino',
  dataIndex: 'destinationAddress',
};

const deliveryTermColumn = {
  title: 'Plazo',
  dataIndex: 'deliveryTerm',
};

const statusColumn = {
  title: 'Estado',
  dataIndex: 'statusDescription',
};

const packageTypeColumn = {
  title: 'Tipo',
  dataIndex: 'packageType',
};

const scheduledDateColumn = {
  title: 'Fecha Programada',
  dataIndex: 'scheduledDate',
};

const sizeColumn = {
  title: 'Tamaño',
  dataIndex: 'packageSize',
  align: 'center',
  render: (text) => <SizeIndicator text={text[0]} />,
  sorter: true,
  sortDirections: ['descend'],
};

const editColumnDuplicateInfo = {
  title: 'Duplicar',
  align: 'center',
  render: (_, record) => <DuplicatePackageButton packet={record} />,
};

const viewPackageColumn = {
  align: 'center',
  render: (_, record) => <ViewPackageButton packageId={record.id} />,
};

const paquerSettingsColumn = {
  align: 'center',
  render: (_, record) => (
    <PaquerSettingsButton actions={actions} packageId={record} isBySearchCode />
  ),
};

const dataColumns = [
  externalCodeColumn,
  destinationAddressColumn,
  deliveryTermColumn,
  scheduledDateColumn,
  packageTypeColumn,
  sizeColumn,
  statusColumn,
];

const PackageSearchByCode = () => {
  usePackagesBySearchCode();
  usePaquers();
  const dispatch = useDispatch();
  const globals = useSelector(selectGlobals);
  const packages = useSelector(packagesBySearchCodeItemsSelector);
  const pagination = useSelector(packagesBySearchCodePageableSelector);
  const loaded = useSelector(packagesBySearchCodeLoadedSelector);

  const editColumn = {
    align: 'center',
    render: (_, record) =>
      isFinalStatus(record.status) ? (
        <EditRecordButton
          record={record}
          link={`${SITELINKS.packages.list}/id`}
        />
      ) : (
        <EditDropdownButton
          record={record}
          link={`${SITELINKS.packages.list}/id`}
          onEditAddressSuccess={() => {
            setTimeout(() => {
              dispatch(actions.refreshPage());
            }, 1500);
          }}
        />
      ),
  };

  const actionColumns = [
    paquerSettingsColumn,
    viewPackageColumn,
    editColumn,
    editColumnDuplicateInfo,
  ];

  const columnsLargeDevice = [
    downloadLabelColumn,
    ...dataColumns,
    ...actionColumns,
  ];

  const columnsMediumDevice = [
    downloadLabelColumn,
    externalCodeColumn,
    statusColumn,
    ...actionColumns,
  ];

  const columnsSmallDevice = [
    downloadLabelColumn,
    externalCodeColumn,
    ...actionColumns,
  ];

  useEffect(() => {
    dispatch(paquerActions.initial());
  }, [dispatch]);

  const searchCallback = useCallback(
    (searchText) => {
      const searchCode = searchText.trim();
      if (searchCode) dispatch(actions.updateSearchPackages(searchText));
    },
    [dispatch],
  );
  const paginateCallback = useCallback(
    (pageable) => {
      dispatch(actions.updatePaginationPackages(pageable));
    },
    [dispatch],
  );
  const searcher = {
    key: 'searchByExternalCode',
    onSearching: searchCallback,
    placeholder: 'Buscar por código externo',
  };

  let filteredPackages;
  if (packages) {
    filteredPackages = packages.map((packet) => ({
      ...packet,
      key: packet.id,
      externalCode: packet.externalCode,
      destinationAddress:
        packet.shippingScheduleDestination?.shippingAddress?.addressDetail,
      scheduledDate: packet.shippingScheduleDestination?.scheduledDate
        ? dayjs(packet.shippingScheduleDestination.scheduledDate)
            .tz()
            .format(TableDateTimeFormat)
        : null,
      deliveryTerm:
        packet.deliveryTerm &&
        globals.deliveryTerm.find((term) => term.value === packet.deliveryTerm)
          .description,
      packageSize: globals.packages.size.find(
        (packSize) => packSize.value === packet.packageSize,
      ).name,
      packageType: globals.packages.type.find(
        (packType) => packType.value === packet.packageType,
      ).name,
      statusDescription: globals.packages.status.find(
        (state) => state.value === packet.status,
      ).name,
      isDeliverable: isReadyForDeliveryStatus(packet.status),
    }));
  }
  return (
    <PaqueryTable
      loading={!loaded}
      header={{
        searcher,
        refresh: () => dispatch(actions.refreshPage()),
        title: 'Buscar por código',
      }}
      usePackageRowColors
      onChangePaginate={paginateCallback}
      dataSource={filteredPackages}
      paginate={pagination}
      dataColumns={dataColumns}
      colsForSmallDevice={columnsSmallDevice}
      colsForMediumDevice={columnsMediumDevice}
      colsForLargeDevice={columnsLargeDevice}
    />
  );
};

export default PackageSearchByCode;
